import { useLayoutEffect, useRef } from "react";
import scrollIntoView from "scroll-into-view-if-needed";

import type {
  ChatMessageContextItem,
  FlaggedContent,
} from "@/react/types/FlaggedContent";

import { FlaggedChatMessage } from "./FlaggedChatMessage";

const SCROLL_CONTAINER_SELECTOR = "[data-flagged-chat-scroll-container]";

const scrollFlaggedMessageIntoView = (el: HTMLElement | null) => {
  if (!el) return;
  const scrollParent = el.closest(SCROLL_CONTAINER_SELECTOR);
  scrollIntoView(el, {
    block: "center",
    scrollMode: "if-needed",
    behavior: "auto",
    ...(scrollParent && { boundary: scrollParent }),
  });
};

interface FlaggedChatMessageBodyProps {
  flaggedContent: FlaggedContent;
}

const renderContextMessage = (item: ChatMessageContextItem) => {
  const { id, author, sent_at, tiptap_body } = item || {};

  return (
    <FlaggedChatMessage
      key={id}
      author={author}
      sentAt={sent_at}
      tiptapBody={tiptap_body}
    />
  );
};

export const FlaggedChatMessageBody = ({
  flaggedContent,
}: FlaggedChatMessageBodyProps) => {
  const flaggedMessageRef = useRef<HTMLDivElement>(null);
  const { author, flagged_contentable } = flaggedContent;
  const {
    sent_at,
    tiptap_body,
    context_messages_before = [],
    context_messages_after = [],
  } = flagged_contentable ?? {};
  useLayoutEffect(() => {
    const el = flaggedMessageRef.current;
    const run = () => scrollFlaggedMessageIntoView(el);
    run();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const observer =
      el && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scrollFlaggedMessageIntoView(el))
        : null;
    if (el && observer) observer.observe(el);
    return () => {
      cancelAnimationFrame(id);
      observer?.disconnect();
    };
  }, [flaggedContent?.id]);

  const hasContextBefore = context_messages_before.length > 0;
  const hasContextAfter = context_messages_after.length > 0;

  return (
    <div className="relative flex flex-col gap-1 border border-primary rounded-md p-2">
      {hasContextBefore && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-18 rounded-t-md bg-gradient-to-b from-primary to-transparent" />
      )}
      {context_messages_before.map(renderContextMessage)}
      <div ref={flaggedMessageRef}>
        <FlaggedChatMessage
          author={author}
          isFlagged
          sentAt={sent_at}
          tiptapBody={tiptap_body}
        />
      </div>
      {context_messages_after.map(renderContextMessage)}
      {hasContextAfter && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-18 rounded-b-md bg-gradient-to-t from-primary to-transparent" />
      )}
    </div>
  );
};
